import type { GlbOptimizeRequest, GlbOptimizeResponse } from '../../workers/glb-optimize.worker';

const OPTIMIZE_TIMEOUT_MS = 45_000;

let worker: Worker | null = null;
let nextId = 1;

const getWorker = (): Worker => {
	worker = worker ?? new Worker(new URL('../../workers/glb-optimize.worker.ts', import.meta.url), { type: 'module' });
	return worker;
};

/** Fire-and-forget encoder init so the first real optimize doesn't pay the wasm fetch+compile */
export const warmOptimizeWorker = (): void => {
	try {
		const request: GlbOptimizeRequest = { id: 0, warmup: true };
		getWorker().postMessage(request);
	} catch {
		// warmup is best-effort — a failed worker falls back to raw upload later anyway
	}
};

/**
 * Draco-compress a raw GLB export off the main thread (same recipe as the api's
 * optimize-glb-buffer). Returns null on any failure or timeout — callers fall
 * back to uploading the raw buffer, so optimization can never block an upload.
 */
export const optimizeGlbInWorker = (glb: ArrayBuffer): Promise<ArrayBuffer | null> => {
	return new Promise<ArrayBuffer | null>((resolve) => {
		let settled = false;
		const w = getWorker();
		const id = nextId++;
		const settle = (result: ArrayBuffer | null): void => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			w.removeEventListener('message', onMessage);
			w.removeEventListener('error', onError);
			resolve(result);
		};
		const onMessage = (event: MessageEvent<GlbOptimizeResponse>): void => {
			if (event.data.id !== id) return;
			if (event.data.error || !event.data.glb) {
				console.warn(`[GlbOptimize] worker optimize failed: ${event.data.error ?? 'no output'}`);
				settle(null);
				return;
			}
			settle(event.data.glb);
		};
		const onError = (event: ErrorEvent): void => {
			console.warn(`[GlbOptimize] worker error: ${event.message}`);
			// A crashed worker stays broken — drop it so the next call gets a fresh one
			worker?.terminate();
			worker = null;
			settle(null);
		};
		const timer = setTimeout(() => settle(null), OPTIMIZE_TIMEOUT_MS);
		w.addEventListener('message', onMessage);
		w.addEventListener('error', onError);
		// Structured clone (no transfer) — the caller keeps the raw buffer as its fallback
		const request: GlbOptimizeRequest = { id, glb };
		w.postMessage(request);
	});
};
